import { useOnScreen, useTranslate } from '@festina-life/ui/hooks'
import cx from 'classnames'
import { type ComponentType, useRef } from 'react'

import { ERROR_TYPE } from '../../scenes/template/TemplateNavigationControls'
import { StackableTableRowActions } from './StackableTableRowActions'
import {
  isStackableType,
  type RowActions,
  type StackableType,
  type ValidStackableRatios,
} from './types'

type Props<D> = {
  /**
   * Component used for rendering a row cell.
   */
  tableCell: ComponentType<{
    data: D
    column: number
    row?: number
  }>
  /**
   * Data for the row being rendered
   */
  data: StackableType<D>
  /**
   * Row index being rendered
   */
  rowIndex?: number
  /**
   * Click handler
   */
  onClick?: (element: StackableType<D>) => void
  /**
   * External classname set on each row
   */
  rowClassName?: string
  /**
   * Width in Px of the first column of the table before applying the indentation. Indentations make the first column narrower
   * by stepSizePx * indentationStep.
   */
  mainColumnWidthPx?: number
  /** Current number of indentation steps for the table. */
  indentationStep: number
  /** Size in Px of each indentation step.*/
  stepSizePx?: number
  /**
   * Column ratios used for rows with empty values.
   */
  emptyRowColumnRatios: readonly ValidStackableRatios[]
  actions?: RowActions<D>
}

/**
 * Renders an individual table row inside a StackTableComponent.
 */
export const StackableTableRow = <S,>({
  rowIndex,
  tableCell: TableCell,
  data,
  rowClassName,
  stepSizePx = 30,
  mainColumnWidthPx = 350,
  indentationStep,
  onClick,
  emptyRowColumnRatios,
  actions,
}: Props<S>) => {
  const translate = useTranslate()
  const rowRef = useRef<HTMLTableRowElement>(null)
  const onScreen = useOnScreen(rowRef)
  const didLoad = useRef(onScreen)
  const columnsForEmptyField = emptyRowColumnRatios.length + 1
  const columnsForCurrentField =
    (data && isStackableType(data) && data.columns) || columnsForEmptyField
  const ratios =
    (data && isStackableType(data) && data.ratios) || emptyRowColumnRatios

  const isStandardRow = columnsForEmptyField === columnsForCurrentField

  const columnStyle = (index: number) => {
    if (index > 0) {
      return
    }

    const currentStepColumnWidth =
      mainColumnWidthPx - stepSizePx * indentationStep

    if (isStandardRow) {
      return {
        width: `${currentStepColumnWidth}px`,
      }
    } else {
      if (columnsForCurrentField === 1) {
        return { width: '100%' }
      }

      const flexSpaces = emptyRowColumnRatios.reduce(
        (acc: number, value) => acc + value,
        0,
      )

      return {
        // +1 px because we can't do a Math.ceil operation with calc
        width: `calc(${currentStepColumnWidth}px + max(150px, calc(100% - ${currentStepColumnWidth}px)/${flexSpaces} + 1px))`,
      }
    }
  }

  didLoad.current = didLoad.current || onScreen

  return (
    <tr
      className={cx(
        'relative flex cursor-pointer overflow-visible group',
        {
          'bg-slate-300 dark:bg-slate-700': data.isActive,
          'bg-error-400 dark:bg-error-900':
            !!data.errors.length && !data.isActive,
          'hover:bg-slate-100! dark:hover:bg-slate-700!': !data.errors.length,
        },
        rowClassName,
      )}
      data-type={data.errors.length ? ERROR_TYPE : undefined}
      onClick={onClick ? () => onClick(data) : undefined}
      ref={rowRef}
    >
      {Array.from(Array(columnsForCurrentField).keys()).map((columnIndex) => {
        const doesColumnHaveContent =
          !data.isGrouped || rowIndex === 0 || columnIndex > 0
        const isContentVisible = didLoad.current ? true : columnIndex === 0
        const columnContent = isContentVisible ? (
          <TableCell data={data.value} column={columnIndex} row={rowIndex} />
        ) : (
          // For performance reasons, given that we can have thousands of loading elements, we don't want to show a loading spinner and instead show static text here.
          <span>{translate('stackableTable.loading')}</span>
        )

        return (
          <td
            key={`cell-${columnIndex}`}
            className={cx(
              `flex items-center border-slate-300 dark:border-slate-500 border-dashed`,
              {
                'overflow-hidden':
                  columnIndex === 0 || !data.shouldShowOnOverflow,
                'border-r': columnIndex < columnsForCurrentField - 1,
                'border-t':
                  rowIndex !== undefined &&
                  (!data.isGrouped || columnIndex > 0),
                'flex-1': columnIndex > 0 && ratios[columnIndex - 1] === 1,
                'flex-2': columnIndex > 0 && ratios[columnIndex - 1] === 2,
                'flex-4': columnIndex > 0 && ratios[columnIndex - 1] === 4,
                'flex-5': columnIndex > 0 && ratios[columnIndex - 1] === 5,
                'min-w-[150px]': columnIndex === 1 && isStandardRow,
              },
            )}
            style={columnStyle(columnIndex)}
          >
            <div
              className={`flex size-full items-center align-middle ${
                data.isEmpty
                  ? 'text-gray-500/40 dark:text-amaze-inkBright/40'
                  : ''
              } ${columnIndex > 0 ? 'justify-center' : ''}`}
            >
              {doesColumnHaveContent && columnContent}
            </div>
          </td>
        )
      })}
      {!!actions?.length && didLoad.current && (
        <StackableTableRowActions actions={actions} data={data} />
      )}
    </tr>
  )
}
